import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Search, Download } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

// Same fixed-order hue rotation used across the app's other lists.
const ROW_HUES = ["bg-fun-blue", "bg-fun-orange", "bg-fun-aqua", "bg-fun-violet", "bg-fun-magenta", "bg-fun-green"];

const TONES = {
  New: "bg-muted text-foreground/70",
  Messaged: "bg-fun-blue-tint text-fun-blue",
  Responded: "bg-fun-yellow-tint text-fun-yellow",
  Registered: "bg-fun-orange-tint text-fun-orange",
  Confirmed: "bg-fun-aqua-tint text-fun-aqua",
  Attended: "bg-primary text-primary-foreground",
  "No-Show": "bg-destructive/10 text-destructive",
};

export default function Attendance() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  async function load() {
    const [{ data: e }, { data: r }] = await Promise.all([
      api.get(`/events/${eventId}`),
      api.get(`/events/${eventId}/attendance`),
    ]);
    setEvent(e);
    setRows(r);
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  async function mark(lead_id, status) {
    try {
      await api.post("/attendance/mark", { event_id: eventId, lead_id, status });
      setRows((prev) =>
        prev.map((r) => (r.id === lead_id ? { ...r, event_status: status } : r)),
      );
      toast.success(status === "Attended" ? "Marked as attended" : "Marked as no-show");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function download() {
    const token = localStorage.getItem("wa_crm_token");
    const res = await fetch(`${API_BASE}/reports/attendance/${eventId}.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `attendance_${eventId}.csv`;
    a.click();
  }

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return r.name.toLowerCase().includes(s) || r.phone.includes(s);
  });

  const stats = {
    total: rows.length,
    attended: rows.filter((r) => r.event_status === "Attended").length,
    noshow: rows.filter((r) => r.event_status === "No-Show").length,
    pending: rows.filter((r) => !["Attended", "No-Show"].includes(r.event_status)).length,
  };

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-8" data-testid="attendance-page">
      <Link
        to="/events"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to events
      </Link>
      <PageHeader
        overline="Event day"
        title="Attendance"
        subtitle={event?.name}
        actions={
          <Button variant="outline" onClick={download} data-testid="attendance-export-btn">
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
        }
      />

      <div className="mt-6 grid grid-cols-4 gap-3">
        <StatMini
          label="Confirmed"
          value={rows.filter((r) => r.event_status === "Confirmed").length}
          tone="text-fun-aqua"
        />
        <StatMini label="Attended" value={stats.attended} tone="text-fun-green" />
        <StatMini label="No-show" value={stats.noshow} tone="text-fun-red" />
        <StatMini label="Pending" value={stats.pending} tone="text-fun-yellow" />
      </div>

      <Card className="mt-6 border-border p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or phone…"
            className="pl-9"
            data-testid="attendance-search"
          />
        </div>
      </Card>

      <div className="mt-4 space-y-2">
        {filtered.length === 0 && (
          <Card className="p-10 text-center text-sm text-muted-foreground">
            No confirmed attendees to mark. Confirmed leads appear here.
          </Card>
        )}
        {filtered.map((r, i) => (
          <Card
            key={r.id}
            className="flex items-center gap-3 border-border p-3 shadow-sm"
            data-testid={`attendance-row-${r.id}`}
          >
            <div
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${ROW_HUES[i % ROW_HUES.length]}`}
            >
              {r.name.slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{r.name}</div>
              <div className="truncate font-mono text-xs text-muted-foreground">{r.phone}</div>
              <Badge className={`mt-1 ${TONES[r.event_status]} rounded-full`}>
                {r.event_status}
              </Badge>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={() => mark(r.id, "Attended")}
                className="min-w-[110px]"
                data-testid={`attend-btn-${r.id}`}
              >
                <Check className="mr-1.5 h-4 w-4" /> Visited
              </Button>
              <Button
                variant="outline"
                onClick={() => mark(r.id, "No-Show")}
                className="min-w-[110px]"
                data-testid={`noshow-btn-${r.id}`}
              >
                <X className="mr-1.5 h-4 w-4" /> No-show
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function StatMini({ label, value, tone = "" }) {
  return (
    <Card className="border-border p-3 text-center shadow-sm">
      <div className={`font-heading text-2xl font-extrabold ${tone}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </Card>
  );
}
